import * as Sentry from "@client/telemetry/sentry";
import { registerBootProperties } from "@client/telemetry/posthog";
import { captureClientEvent } from "@client/utils/analyticsEvent";
import { indexedDBWasMissingAtBoot } from "@client/offline/hasIndexedDB";

/**
 * What Dexie is backed by in this session, decided once at boot by the entry
 * (web or native) before anything opens a database:
 * - `indexeddb`: the browser's real IndexedDB.
 * - `lockdown-standin`: iPad shell under iOS Lockdown Mode — no IndexedDB, so
 *   fake-indexeddb mirrored to native snapshot files.
 * - `memory-standin`: no IndexedDB and no native files (Safari Lockdown Mode
 *   on the web, the iPhone shell under Lockdown) — fake-indexeddb, memory only.
 * - `unavailable`: no IndexedDB and the stand-in chunk failed to load.
 */
export type StorageBackend =
  | "indexeddb"
  | "lockdown-standin"
  | "memory-standin"
  | "unavailable";

const LAST_BACKEND_KEY = "sfc.storageBackend.last";

const STORAGE_BACKENDS: readonly StorageBackend[] = [
  "indexeddb",
  "lockdown-standin",
  "memory-standin",
  "unavailable",
];

function isStorageBackend(value: string): value is StorageBackend {
  return (STORAGE_BACKENDS as readonly string[]).includes(value);
}

/**
 * Report the session's storage backend everywhere the question "is this
 * device in Lockdown Mode?" gets asked (SP-2757):
 * - the Sentry tag, so error events carry it (this is how it was found that
 *   the funder's iPad runs real IndexedDB, not the stand-in we assumed);
 * - a PostHog super property, so EVERY event and session recording carries it
 *   and it is queryable per person on demand — Sentry only sees errors, and
 *   only from devices armed to report them;
 * - a one-off "storage backend changed" event when the value differs from
 *   the last boot's, so a Lockdown toggle shows up as a change and not only
 *   as a state — emitted by `reportAppLaunch`, once the current build is
 *   registered, so the event names the build it happened on.
 *
 * Both entries call this once, right after deciding the backend, and then
 * `reportAppLaunch`; keeping the reports in one place is what stops the two
 * entries drifting apart again.
 */
let reportedBackend: StorageBackend | null = null;
/** A backend change detected at boot, held until the launch report so the
 * event carries the CURRENT build (super properties persist across launches;
 * emitting before the build is registered would attribute the change to the
 * previous launch's build). */
let pendingChange: { from: StorageBackend; to: StorageBackend } | null = null;

export function reportStorageBackend(backend: StorageBackend): void {
  reportedBackend = backend;
  Sentry.setTag("storage_backend", backend);
  // Both as super properties, so the initial pageview and every later event
  // carry them and a session breakdown can tell Lockdown from a lone broken
  // database without needing the launch event.
  registerBootProperties({
    storage_backend: backend,
    lockdown_signals: lockdownSignals(),
    // The launch keys are registered here too, as "unknown yet", so that no
    // event emitted before `reportAppLaunch` (a feature-flag exposure, say)
    // can carry the PREVIOUS launch's persisted build; the launch report
    // overwrites them with this launch's values.
    app_version: null,
    app_build: null,
    sentry_native_armed: null,
  });

  const previous = readLastBackend();
  if (previous != null && previous !== backend) {
    pendingChange = { from: previous, to: backend };
  }
  writeLastBackend(backend);
}

function readLastBackend(): StorageBackend | null {
  try {
    const value = window.localStorage.getItem(LAST_BACKEND_KEY);
    return value != null && isStorageBackend(value) ? value : null;
  } catch {
    return null;
  }
}

function writeLastBackend(backend: StorageBackend): void {
  try {
    window.localStorage.setItem(LAST_BACKEND_KEY, backend);
  } catch {
    // Storage unavailable (private mode, quota): the tag and property above
    // are the reports that matter; the change event is best-effort.
  }
}

/**
 * Web features Lockdown Mode removes from the WebView, as observed at boot.
 * Independent witnesses next to the storage backend: on an iPad under
 * Lockdown Mode (iOS 26.6.1, checked 2026-09-04) IndexedDB, WebAssembly and
 * WebGL are all absent, so a session missing only IndexedDB is a broken
 * database, not Lockdown. IndexedDB is judged from the boot-time snapshot
 * because the stand-ins install a fake global before this runs.
 */
export function lockdownSignals(): string[] {
  const signals: string[] = [];
  if (indexedDBWasMissingAtBoot) signals.push("no-indexeddb");
  if (typeof WebAssembly === "undefined") signals.push("no-webassembly");
  if (typeof document !== "undefined" && !hasWebGL()) signals.push("no-webgl");
  return signals;
}

/**
 * One explicit event per launch, plus the launch-scoped super properties.
 * Super properties only ride on events, and a launch where the funder opens
 * the app, reads, and leaves may capture nothing else — this is the record
 * that says a launch happened and what the device looked like. Call once per
 * boot after `reportStorageBackend`; the native entry calls it once the app
 * info has resolved (or failed).
 */
export function reportAppLaunch({
  appVersion,
  appBuild,
  sentryNativeArmed,
}: {
  /** Native marketing version (e.g. "1.0"); absent on the web. */
  appVersion?: string;
  /** Native build number, or the web deploy's release id. */
  appBuild?: string;
  /** Native only: whether the device had armed Sentry reporting at boot. */
  sentryNativeArmed?: boolean;
}): void {
  // Every key is registered every launch, with `null` for what this launch
  // could not determine: super properties persist across launches, so
  // skipping a key would leave the PREVIOUS launch's value on this one's
  // events (a failed native app-info call would report last time's build).
  const launchProperties = {
    app_version: appVersion ?? null,
    app_build: appBuild ?? null,
    sentry_native_armed: sentryNativeArmed ?? null,
  };
  registerBootProperties(launchProperties);

  // Only now, with the current build registered, can a change event be
  // attributed correctly.
  if (pendingChange != null) {
    const change = pendingChange;
    pendingChange = null;
    captureClientEvent({
      event: "storage backend changed",
      sourceFamily: "storage",
      properties: { from: change.from, to: change.to },
    });
  }

  captureClientEvent({
    event: "app launched",
    sourceFamily: "app_lifecycle",
    properties: {
      storage_backend: reportedBackend,
      lockdown_signals: lockdownSignals(),
      ...launchProperties,
    },
  });
}

function hasWebGL(): boolean {
  try {
    return document.createElement("canvas").getContext("webgl") != null;
  } catch {
    return false;
  }
}
